import {compareBrandByPosition, getAllStorageHighlightBrand} from '@/_application/modules/brand/helpers/brand-data';
import {Product} from '@/_application/modules/products/model/product';
import {useHighlightBrandsLazyQuery} from '@/framework/gql/highlight-brands.graphql';
import {useCategoryAggregations} from '@/framework/product';
import {AggregationOption, HighlightBrand, HighlightBrands} from '__generated__/__types__';
import {useEffect} from 'react';

export function useHighlightBrands() {
    const {brands, loading, error} = useBrands();

    const filteredBrands = (brands?.items?.filter(brand => brand) ?? []) as HighlightBrand[];
    const sortedBrands = filteredBrands.sort(compareBrandByPosition);

    return {
        highlightBrands: sortedBrands,
        homePageConfig: brands?.homepage_layout ?? {},
        categoryConfig: brands?.category_layout ?? {},
        isLoading: loading,
        error,
    };
}

export function useProductBrand(product: Product) {
    const {brands, loading, error} = useBrands();
    const highlightBrands = brands?.items ?? [];
    const productBrand = highlightBrands?.find(brand => brand?.id === product?.brand) ?? {};

    return {
        brand: productBrand,
        isLoading: loading,
        error,
    };
}

export function useBrandsByOptions(options: AggregationOption[]) {
    const {brands, loading, error} = useBrands();
    const highlightBrands = brands?.items ?? [];

    const filterBrands = (options
        ?.filter(option => option)
        ?.map(option => {
            const matchingBrand = highlightBrands?.find(brand => brand?.label === option?.value);
            return matchingBrand;
        }) ?? []) as HighlightBrand[];

    const sortedBrands = filterBrands.sort(compareBrandByPosition);

    return {
        brands: sortedBrands,
        isLoading: loading,
        error,
    };
}

export function useCategoryBrands(categoryId: number) {
    const {data: aggregations, isLoading: isLoadingAggregations} = useCategoryAggregations(categoryId);
    const brandOptions = aggregations?.find(a => a?.attribute_code === 'brand');
    const options = brandOptions?.options?.filter(option => option) ?? [];

    const {brands, isLoading: isLoadingBrand, error} = useBrandsByOptions(options as AggregationOption[]);

    return {
        brands,
        isLoading: isLoadingBrand || isLoadingAggregations,
        error,
    };
}

export const useBrands = () => {
    const storageBrands = getAllStorageHighlightBrand();
    const [fetchHighlightBrands, {data, loading, error}] = useHighlightBrandsLazyQuery();

    useEffect(() => {
        if (!storageBrands) {
            fetchHighlightBrands();
        }
    }, []);

    return {
        brands: (storageBrands ?? data?.highlightBrands ?? []) as HighlightBrands,
        loading: loading,
        error,
    };
};
